import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtDateOnly, formatError } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, TextArea, EmptyState, Badge, SearchBar, normalize } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, RotateCcw, HardHat, Printer, Trash2 } from "lucide-react";

const empty = { person_tipo: "colaborador", collaborator_id: "", cooperado_id: "", product_id: "", quantidade: 1, data_validade: "", observacoes: "", ca_individual: "" };

export default function EpiDeliveries() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("almoxarife");
  const [items, setItems] = useState([]);
  const [collaborators, setCollabs] = useState([]);
  const [cooperados, setCoops] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const [e, c, co, p] = await Promise.all([
      api.get("/epi-deliveries"), api.get("/collaborators"), api.get("/cooperados"),
      api.get("/products?categoria=EPI"),
    ]);
    setItems(e.data); setCollabs(c.data); setCoops(co.data); setProducts(p.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        person_tipo: form.person_tipo,
        collaborator_id: form.person_tipo === "colaborador" ? form.collaborator_id : null,
        cooperado_id: form.person_tipo === "cooperado" ? form.cooperado_id : null,
        product_id: form.product_id,
        quantidade: parseFloat(form.quantidade),
        data_validade: form.data_validade || null,
        observacoes: form.observacoes,
        ca_individual: (form.ca_individual || "").trim(),
      };
      await api.post("/epi-deliveries", payload);
      toast.success("Entrega registrada");
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const returnEpi = async (id) => {
    if (!confirm("Registrar devolução do EPI?")) return;
    try { await api.post(`/epi-deliveries/${id}/return`); toast.success("Devolução registrada"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const removeEpi = async (e) => {
    const warn = e.status === "entregue"
      ? `Excluir entrega ${e.product_nome} → ${e.collaborator_nome}?\n\nO estoque será restaurado automaticamente.`
      : `Excluir esta entrega definitivamente?`;
    if (!confirm(warn)) return;
    try {
      await api.delete(`/epi-deliveries/${e.id}`);
      toast.success("Entrega excluída");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const now = new Date();
  const venc = (d) => {
    if (!d) return null;
    const dv = new Date(d);
    if (dv < now) return "vencido";
    const diff = (dv - now) / (1000 * 60 * 60 * 24);
    if (diff < 30) return "a_vencer";
    return "ok";
  };

  const filtered = items.filter((e) => {
    if (statusFilter && e.status !== statusFilter) return false;
    if (!q) return true;
    const nq = normalize(q);
    return normalize(e.collaborator_nome).includes(nq) || normalize(e.product_nome).includes(nq) || normalize(e.product_ca).includes(nq) || normalize(e.ca_individual).includes(nq) || normalize(e.product_fabricante).includes(nq) || normalize(e.sector_nome).includes(nq);
  });

  return (
    <div data-testid="epis-page">
      <SectionTitle action={<Btn onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-epi-btn"><Plus className="w-4 h-4" />Nova Entrega</Btn>}>
        Entrega de EPIs · <HardHat className="inline w-5 h-5 -mt-1" />
      </SectionTitle>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <SearchBar value={q} onChange={setQ} placeholder="Buscar por recebedor, EPI, CA..." testid="epis-search" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border-2 border-[#111] p-2 text-sm bg-white font-mono-data" data-testid="epis-status-filter">
          <option value="">Todos</option>
          <option value="entregue">Em uso</option>
          <option value="devolvido">Devolvidos</option>
        </select>
        <span className="text-xs font-mono-data text-[#555] ml-auto uppercase tracking-widest">
          <b className="text-base text-[#111]">{filtered.length}</b> / {items.length}
        </span>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th>Data</th><th>Recebedor</th><th>Tipo</th><th>EPI</th><th>CA / Validade</th><th>Fabricante</th><th className="text-right">Qtd</th><th>Validade Uso</th><th>Status</th><th className="w-24">Ações</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && filtered.length === 0 && <EmptyState message={q || statusFilter ? "Nenhum resultado." : "Nenhuma entrega registrada."} cols={10} />}
            {filtered.map((e) => {
              const v = venc(e.data_validade);
              const caVal = e.product_ca_validade;
              return (
                <tr key={e.id}>
                  <td className="font-mono-data text-xs">{fmtDate(e.data_entrega)}</td>
                  <td className="font-medium">{e.collaborator_nome}</td>
                  <td><Badge variant={e.person_tipo === "cooperado" ? "orange" : "green"}>{e.person_tipo === "cooperado" ? "Cooperado" : "Colaborador"}</Badge></td>
                  <td>{e.product_nome}<div className="text-[10px] text-[#777]">{e.sector_nome || ""}</div></td>
                  <td className="font-mono-data text-xs">
                    {e.product_ca || e.ca_individual ? (
                      <>
                        <div className="font-bold">{e.product_ca || e.ca_individual}</div>
                        {caVal && <div className="text-[#555]">{new Date(caVal).toLocaleDateString("pt-BR")}</div>}
                      </>
                    ) : "—"}
                  </td>
                  <td className="text-xs text-[#555]">{e.product_fabricante || "—"}</td>
                  <td className="text-right font-mono-data font-bold">{e.quantidade}</td>
                  <td className="font-mono-data text-xs">{fmtDateOnly(e.data_validade)}</td>
                  <td>
                    {e.status === "devolvido" ? <Badge>Devolvido</Badge>
                      : v === "vencido" ? <Badge variant="danger">Vencido</Badge>
                      : v === "a_vencer" ? <Badge variant="warn">A vencer</Badge>
                      : <Badge variant="success">Em uso</Badge>}
                  </td>
                  <td><div className="flex gap-1">
                    <a href={`/imprimir/epi/${e.id}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111] inline-flex" title="Imprimir ficha"><Printer className="w-3.5 h-3.5" /></a>
                    {e.status === "entregue" && <button onClick={() => returnEpi(e.id)} className="p-1.5 hover:bg-[#1A7F37] hover:text-white border border-[#111]" title="Devolver"><RotateCcw className="w-3.5 h-3.5" /></button>}
                    {canEdit && <button onClick={() => removeEpi(e)} data-testid={`delete-epi-${e.id}`} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]" title="Excluir entrega"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Nova Entrega de EPI" size="md">
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="epi-form">
          <div className="col-span-2">
            <label className="block font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Tipo de Recebedor *</label>
            <div className="flex gap-0 border-2 border-[#111]">
              {[{ v: "colaborador", l: "Colaborador" }, { v: "cooperado", l: "Cooperado" }].map((opt, i) => (
                <button key={opt.v} type="button"
                  onClick={() => setForm({ ...form, person_tipo: opt.v, collaborator_id: "", cooperado_id: "" })}
                  className={`flex-1 py-2 font-display font-bold uppercase text-xs tracking-widest ${i > 0 ? "border-l-2 border-[#111]" : ""} ${form.person_tipo === opt.v ? "bg-[#FF4F00] text-white" : "bg-white text-[#111]"}`}
                  data-testid={`epi-tipo-${opt.v}`}>
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
          {form.person_tipo === "colaborador" ? (
            <SelectField label="Colaborador *" required value={form.collaborator_id} onChange={(v) => setForm({ ...form, collaborator_id: v })}
              options={collaborators.map((c) => ({ value: c.id, label: `${c.nome}${c.sector_nome ? " · " + c.sector_nome : ""}` }))} colspan />
          ) : (
            <SelectField label="Cooperado *" required value={form.cooperado_id} onChange={(v) => setForm({ ...form, cooperado_id: v })}
              options={cooperados.map((c) => ({ value: c.id, label: `${c.nome}${c.community_nome ? " · " + c.community_nome : ""}` }))} colspan />
          )}
          <SelectField label="EPI *" required value={form.product_id} onChange={(v) => setForm({ ...form, product_id: v })}
            options={products.filter((p) => p.estoque_atual > 0).map((p) => ({ value: p.id, label: `${p.codigo} · ${p.nome}${p.ca ? " · " + p.ca : ""} (${p.estoque_atual} disp.)` }))} colspan />
          {form.product_id && (() => {
            const sel = products.find((p) => p.id === form.product_id);
            if (!sel) return null;
            const dv = sel.ca_validade ? new Date(sel.ca_validade) : null;
            const diasCa = dv ? Math.floor((dv - new Date()) / (1000*60*60*24)) : null;
            const caExpired = diasCa !== null && diasCa < 0;
            const caSoon = diasCa !== null && diasCa >= 0 && diasCa <= 30;
            return (
              <div className={`col-span-2 brutal-card-sm p-3 ${caExpired ? "bg-[#FEE] border-2 border-[#E60000]" : caSoon ? "bg-[#FFF8E1] border-2 border-[#FFC107]" : "bg-[#E8F0EA] border-2 border-[#3D6B57]"}`}>
                <div className="font-display font-bold uppercase text-[11px] tracking-widest mb-2">🦺 Dados do EPI (auto-preenchido)</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-[#555]">CA:</span> <b className="font-mono-data">{sel.ca || "— sem CA cadastrado —"}</b></div>
                  <div>
                    <span className="text-[#555]">Validade CA:</span> <b className="font-mono-data">{sel.ca_validade ? new Date(sel.ca_validade).toLocaleDateString("pt-BR") : "—"}</b>
                    {caExpired && <span className="ml-2 text-[#E60000] font-bold">⚠️ CA VENCIDO</span>}
                    {caSoon && <span className="ml-2 text-[#7C2D12] font-bold">⚠️ vence em {diasCa}d</span>}
                  </div>
                  <div><span className="text-[#555]">Fabricante:</span> <b>{sel.fabricante || "—"}</b></div>
                  <div><span className="text-[#555]">Lote:</span> <b className="font-mono-data">{sel.lote || "—"}</b></div>
                </div>
                {caExpired && <div className="mt-2 text-[11px] text-[#E60000] font-bold">⛔ CA do produto VENCIDO. Atualize o cadastro antes de entregar.</div>}
              </div>
            );
          })()}
          <Field label="Quantidade *" type="number" required value={form.quantidade} onChange={(v) => setForm({ ...form, quantidade: v })} />
          <Field label="Validade da entrega (em uso)" type="date" value={form.data_validade} onChange={(v) => setForm({ ...form, data_validade: v })} />
          <Field label="CA específico desta entrega (opcional)" value={form.ca_individual} onChange={(v) => setForm({ ...form, ca_individual: v })} placeholder="Ex: CA 12345 · deixe em branco pra herdar do produto" colspan testid="epi-ca-individual" />
          <TextArea label="Observações" value={form.observacoes} onChange={(v) => setForm({ ...form, observacoes: v })} colspan />
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-epi-btn">Registrar</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
